import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { adminService } from '@/services/admin.service';
import { examsService } from '@/services/exams.service';
import { coursesService } from '@/services/courses.service';
import { questionsService } from '@/services/questions.service';
import { contentService } from '@/services/content.service';
import {
  FileText,
  GraduationCap,
  BookOpen,
  CheckCircle,
  Clock,
  Activity as ActivityIcon,
  TrendingUp,
  Sparkles,
  RefreshCw,
  Zap,
  ArrowUp,
  Users,
  Award,
  Calendar,
  Eye,
} from 'lucide-react';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts';
import { DashboardSkeleton } from '@/components/LoadingSkeleton';
import { PageHeader } from '@/components/PageHeader';

// Counter Animation Hook
function useCountUp(end: number, duration: number = 2000) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime: number;
    let animationFrame: number;

    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);

      setCount(Math.floor(progress * end));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [end, duration]);

  return count;
}

export function Dashboard() {
  const queryClient = useQueryClient();
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Initialize count state for animations (must be before queries to maintain hook order)
  const [examsCountState, setExamsCountState] = useState(0);
  const [coursesCountState, setCoursesCountState] = useState(0);
  const [materialsCountState, setMaterialsCountState] = useState(0);
  const [questionsCountState, setQuestionsCountState] = useState(0);

  // Fetch real data from individual services
  const { data: examsData } = useQuery({
    queryKey: ['dashboard-exams'],
    queryFn: async () => {
      try {
        const result = await examsService.getExams({});
        return Array.isArray(result) ? result : [];
      } catch (err) {
        console.error('Failed to fetch exams:', err);
        return [];
      }
    },
    refetchInterval: 30000,
  });

  const { data: coursesData } = useQuery({
    queryKey: ['dashboard-courses'],
    queryFn: async () => {
      try {
        const result = await coursesService.getCourses();
        return Array.isArray(result) ? result : [];
      } catch (err) {
        console.error('Failed to fetch courses:', err);
        return [];
      }
    },
    refetchInterval: 30000,
  });

  const { data: questionsData } = useQuery({
    queryKey: ['dashboard-questions'],
    queryFn: async () => {
      try {
        const result = await questionsService.getQuestions({ limit: 10000 });
        return result?.data?.questions || [];
      } catch (err) {
        console.error('Failed to fetch questions:', err);
        return [];
      }
    },
    refetchInterval: 30000,
  });

  const { data: materialsData } = useQuery({
    queryKey: ['dashboard-materials'],
    queryFn: async () => {
      try {
        const token = localStorage.getItem('accessToken') || '';
        const result = await contentService.getDocuments({}, token);
        return result?.documents || [];
      } catch (err) {
        console.error('Failed to fetch materials:', err);
        return [];
      }
    },
    refetchInterval: 30000,
  });

  // Try to fetch admin stats for health and activity (optional)
  const { data: adminData, isLoading } = useQuery({
    queryKey: ['dashboard-admin-stats'],
    queryFn: async () => {
      try {
        const token = localStorage.getItem('accessToken') || '';
        return await adminService.getSystemStats(token);
      } catch (err) {
        console.error('Failed to fetch admin stats:', err);
        return {
          system_health: {
            cache_hit_rate: 0.85,
            token_usage: { used: 50000, limit: 100000 },
            review_queue_size: 0,
          },
          recent_activity: [],
          generation_trend: [],
        };
      }
    },
    refetchInterval: 30000,
  });

  // Calculate real counts
  const stats = {
    exams_count: examsData?.length || 0,
    courses_count: coursesData?.length || 0,
    materials_count: materialsData?.length || 0,
    questions_count: questionsData?.length || 0,
  };

  // Update count states when data changes
  useEffect(() => {
    setExamsCountState(stats.exams_count);
  }, [stats.exams_count]);

  useEffect(() => {
    setCoursesCountState(stats.courses_count);
  }, [stats.courses_count]);

  useEffect(() => {
    setMaterialsCountState(stats.materials_count);
  }, [stats.materials_count]);

  useEffect(() => {
    setQuestionsCountState(stats.questions_count);
  }, [stats.questions_count]);

  // Animated counters - must be called after all other hooks
  const examsCount = useCountUp(examsCountState);
  const coursesCount = useCountUp(coursesCountState);
  const materialsCount = useCountUp(materialsCountState);
  const questionsCount = useCountUp(questionsCountState);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await queryClient.invalidateQueries({ queryKey: ['dashboard-exams'] });
    await queryClient.invalidateQueries({ queryKey: ['dashboard-courses'] });
    await queryClient.invalidateQueries({ queryKey: ['dashboard-questions'] });
    await queryClient.invalidateQueries({ queryKey: ['dashboard-materials'] });
    await queryClient.invalidateQueries({ queryKey: ['dashboard-admin-stats'] });
    setTimeout(() => setIsRefreshing(false), 1000);
  };

  if (isLoading) {
    return <DashboardSkeleton />;
  }

  // Generate dynamic trend data from actual questions
  const generateTrendData = () => {
    const days = 7;
    const trendData = [];
    const today = new Date();

    // Create buckets for last 7 days
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      date.setHours(0, 0, 0, 0);

      const nextDate = new Date(date);
      nextDate.setDate(nextDate.getDate() + 1);

      // Count questions created on this day
      const count = questionsData?.filter((q: any) => {
        if (!q.created_at) return false;
        const qDate = new Date(q.created_at);
        return qDate >= date && qDate < nextDate;
      }).length || 0;

      trendData.push({
        date: date.toISOString(),
        count: count,
      });
    }

    return trendData;
  };

  const trends = questionsData && questionsData.length > 0 ? generateTrendData() : adminData?.generation_trend || [];

  // Generate dynamic activity from real data
  const generateRecentActivity = () => {
    const activities: any[] = [];

    // Add recent questions
    if (questionsData && questionsData.length > 0) {
      questionsData.slice(0, 3).forEach((question: any) => {
        activities.push({
          id: `question-${question._id}`,
          type: 'generation',
          message: `New question generated for ${question.exam_name || 'exam'}`,
          timestamp: question.created_at || new Date().toISOString(),
        });
      });
    }

    // Add recent exams
    if (examsData && examsData.length > 0) {
      examsData.slice(0, 2).forEach((exam: any) => {
        activities.push({
          id: `exam-${exam._id}`,
          type: 'system',
          message: `Exam "${exam.name}" was created`,
          timestamp: exam.created_at || new Date().toISOString(),
        });
      });
    }

    // Add recent materials
    if (materialsData && materialsData.length > 0) {
      materialsData.slice(0, 2).forEach((material: any) => {
        activities.push({
          id: `material-${material._id}`,
          type: 'system',
          message: `Document "${material.filename || material.title}" was uploaded`,
          timestamp: material.uploaded_at || material.created_at || new Date().toISOString(),
        });
      });
    }

    // Add review activity if available
    const approvedCount = questionsData?.filter((q: any) => q.status === 'approved').length || 0;
    if (approvedCount > 0) {
      activities.push({
        id: 'review-approved',
        type: 'review',
        message: `${approvedCount} questions approved in review`,
        timestamp: new Date().toISOString(),
      });
    }

    // Sort by timestamp (most recent first) and limit to 10
    return activities
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      .slice(0, 10);
  };

  const activities = generateRecentActivity();

  const statCards = [
    {
      name: 'Exams',
      value: stats.exams_count,
      animatedValue: examsCount,
      icon: GraduationCap,
      gradient: 'from-blue-500 to-blue-600',
      bgGradient: 'from-blue-50 to-blue-100',
      change: '+12%',
    },
    {
      name: 'Courses',
      value: stats.courses_count,
      animatedValue: coursesCount,
      icon: BookOpen,
      gradient: 'from-green-500 to-emerald-600',
      bgGradient: 'from-green-50 to-emerald-100',
      change: '+8%',
    },
    {
      name: 'Materials',
      value: stats.materials_count,
      animatedValue: materialsCount,
      icon: FileText,
      gradient: 'from-purple-500 to-purple-600',
      bgGradient: 'from-purple-50 to-purple-100',
      change: '+23%',
    },
    {
      name: 'Questions',
      value: stats.questions_count,
      animatedValue: questionsCount,
      icon: CheckCircle,
      gradient: 'from-orange-500 to-orange-600',
      bgGradient: 'from-orange-50 to-orange-100',
      change: '+45%',
    },
  ];

  return (
    <div className="space-y-8 animate-in">
      {/* Page Header with Breadcrumbs */}
      <div className="flex items-start justify-between">
        <PageHeader
          title="Dashboard"
          description="Welcome back! Here's what's happening today."
          breadcrumbs={[{ label: 'Dashboard' }]}
        />
        <button
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#4A90C8] to-[#3c95b7] text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Enhanced Stats Cards */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        {statCards.map((stat, index) => (
          <div
            key={stat.name}
            className={`relative overflow-hidden rounded-2xl border-2 border-gray-100 p-6 bg-gradient-to-br ${stat.bgGradient} hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer group`}
            style={{ animationDelay: `${index * 100}ms` }}
          >
            {/* Background pattern */}
            <div className="absolute inset-0 opacity-5">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(0,0,0,0.3)_1px,transparent_1px)] bg-[length:20px_20px]" />
            </div>

            <div className="relative">
              <div className="flex items-center justify-between mb-4">
                <div className={`p-3 rounded-xl bg-gradient-to-br ${stat.gradient} shadow-lg transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-300`}>
                  <stat.icon className="w-7 h-7 text-white" />
                </div>
                <div className="flex items-center gap-1 text-green-600 font-bold text-sm">
                  <ArrowUp className="w-4 h-4" />
                  {stat.change}
                </div>
              </div>

              <div>
                <p className="text-sm font-semibold text-gray-600 mb-1">{stat.name}</p>
                <p className="text-4xl font-bold text-gray-900 mb-1">
                  {stat.animatedValue.toLocaleString()}
                </p>
                <p className="text-xs text-gray-500">Total {stat.name.toLowerCase()} in system</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
        {/* Enhanced Recent Activity */}
        <div className="lg:col-span-2 rounded-2xl border-2 border-gray-100 p-6 bg-gradient-to-br from-white to-gray-50 shadow-lg">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <ActivityIcon className="w-6 h-6 text-blue-600" />
              Recent Activity
            </h2>
            <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-bold rounded-full">
              Live
            </span>
          </div>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {activities && activities.length > 0 ? (
              activities.map((activity: any, index: number) => (
                <div
                  key={activity.id}
                  className="flex items-start p-4 space-x-4 transition-all bg-white rounded-xl hover:shadow-md border border-gray-100 hover:border-blue-200 cursor-pointer group"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <div className="mt-1">
                    {activity.type === 'review' && (
                      <div className="p-2 bg-green-100 rounded-lg group-hover:bg-green-200 transition-colors">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      </div>
                    )}
                    {activity.type === 'generation' && (
                      <div className="p-2 bg-blue-100 rounded-lg group-hover:bg-blue-200 transition-colors">
                        <Sparkles className="w-5 h-5 text-blue-600 animate-pulse" />
                      </div>
                    )}
                    {activity.type === 'system' && (
                      <div className="p-2 bg-gray-100 rounded-lg group-hover:bg-gray-200 transition-colors">
                        <Clock className="w-5 h-5 text-gray-600" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                      {activity.message}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(activity.timestamp).toLocaleTimeString()}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12">
                <ActivityIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No recent activity</p>
              </div>
            )}
          </div>
        </div>

        {/* Enhanced Quick Actions */}
        <div className="rounded-2xl border-2 border-gray-100 p-6 bg-gradient-to-br from-white to-orange-50/30 shadow-lg">
          <h2 className="mb-6 text-xl font-bold text-gray-900 flex items-center gap-2">
            <Zap className="w-6 h-6 text-orange-600" />
            Quick Actions
          </h2>
          <div className="space-y-3">
            <Link
              to="/exams/create"
              className="block w-full px-5 py-4 text-left transition-all bg-gradient-to-r from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200 rounded-xl border-2 border-blue-200 hover:border-blue-400 shadow-sm hover:shadow-md transform hover:-translate-y-1 group"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500 rounded-lg group-hover:bg-blue-600 transition-colors">
                  <GraduationCap className="w-5 h-5 text-white" />
                </div>
                <p className="font-bold text-blue-700">Create Exam</p>
              </div>
            </Link>
            <Link
              to="/materials"
              className="block w-full px-5 py-4 text-left transition-all bg-gradient-to-r from-green-50 to-emerald-100 hover:from-green-100 hover:to-emerald-200 rounded-xl border-2 border-green-200 hover:border-green-400 shadow-sm hover:shadow-md transform hover:-translate-y-1 group"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500 rounded-lg group-hover:bg-green-600 transition-colors">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <p className="font-bold text-green-700">Upload PDF</p>
              </div>
            </Link>
            <Link
              to="/generator-center"
              className="block w-full px-5 py-4 text-left transition-all bg-gradient-to-r from-purple-50 to-purple-100 hover:from-purple-100 hover:to-purple-200 rounded-xl border-2 border-purple-200 hover:border-purple-400 shadow-sm hover:shadow-md transform hover:-translate-y-1 group"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-r from-[#1e73be] to-[#ea7500] rounded-lg group-hover:opacity-90 transition-opacity">
                  <Sparkles className="w-5 h-5 text-white animate-pulse" />
                </div>
                <p className="font-bold text-purple-700">Generate Questions</p>
              </div>
            </Link>
            <Link
              to="/analytics"
              className="block w-full px-5 py-4 text-left transition-all bg-gradient-to-r from-orange-50 to-orange-100 hover:from-orange-100 hover:to-orange-200 rounded-xl border-2 border-orange-200 hover:border-orange-400 shadow-sm hover:shadow-md transform hover:-translate-y-1 group"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-500 rounded-lg group-hover:bg-orange-600 transition-colors">
                  <TrendingUp className="w-5 h-5 text-white" />
                </div>
                <p className="font-bold text-orange-700">View Analytics</p>
              </div>
            </Link>
          </div>
        </div>
      </div>

      {/* System Health - Hidden */}
      {/* <div className="rounded-2xl border-2 border-gray-100 p-8 bg-gradient-to-br from-white to-blue-50/30 shadow-lg">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Target className="w-7 h-7 text-blue-600" />
            System Health
          </h2>
          <span className="px-4 py-2 bg-green-100 text-green-700 text-sm font-bold rounded-full flex items-center gap-2">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
            Healthy
          </span>
        </div>
        <div className="space-y-6">
          <div className="p-5 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Zap className="w-5 h-5 text-green-600" />
                </div>
                <span className="text-sm font-bold text-gray-700">Cache Hit Rate</span>
              </div>
              <span className="text-lg font-bold text-green-600">
                {((health?.cache_hit_rate || 0) * 100).toFixed(0)}%
              </span>
            </div>
            <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-3 bg-gradient-to-r from-green-400 to-green-600 rounded-full transition-all duration-1000 ease-out"
                style={{ width: `${(health?.cache_hit_rate || 0) * 100}%` }}
              />
            </div>
          </div>

          <div className="p-5 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <ActivityIcon className="w-5 h-5 text-blue-600" />
                </div>
                <span className="text-sm font-bold text-gray-700">Token Budget</span>
              </div>
              <span className="text-lg font-bold text-blue-600">
                {((health?.token_usage.used || 0) / 1000).toFixed(0)}k /{' '}
                {((health?.token_usage.limit || 0) / 1000).toFixed(0)}k
              </span>
            </div>
            <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-3 bg-gradient-to-r from-blue-400 to-blue-600 rounded-full transition-all duration-1000 ease-out"
                style={{
                  width: `${((health?.token_usage.used || 0) / (health?.token_usage.limit || 1)) * 100}%`,
                }}
              />
            </div>
          </div>

          <div className="p-5 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Clock className="w-5 h-5 text-orange-600" />
                </div>
                <span className="text-sm font-bold text-gray-700">Review Queue</span>
              </div>
              <span className="text-lg font-bold text-orange-600">
                {health?.review_queue_size || 0} pending
              </span>
            </div>
            <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-3 bg-gradient-to-r from-orange-400 to-orange-600 rounded-full transition-all duration-1000 ease-out"
                style={{ width: `${Math.min(((health?.review_queue_size || 0) / 100) * 100, 100)}%` }}
              />
            </div>
          </div>
        </div>
      </div> */}

      {/* Enhanced Generation Trend Chart */}
      <div className="rounded-3xl border-2 border-gray-100 p-8 bg-gradient-to-br from-white via-blue-50/30 to-orange-50/30 shadow-2xl hover:shadow-3xl transition-all duration-300 relative overflow-hidden">
        {/* Animated background pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(30,115,190,0.3)_1px,transparent_1px)] bg-[length:40px_40px] animate-pulse" />
        </div>

        <div className="relative">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gradient-to-br from-[#1e73be] to-[#ea7500] rounded-2xl shadow-lg animate-glow">
                <TrendingUp className="w-8 h-8 text-white" />
              </div>
              <div>
                <h2 className="text-3xl font-bold bg-gradient-to-r from-[#1e73be] to-[#ea7500] bg-clip-text text-transparent flex items-center gap-3">
                  Generation Activity
                </h2>
                <p className="text-gray-600 text-sm mt-1 font-medium">Last 7 days performance tracking</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white text-base font-bold rounded-2xl shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 animate-pulse" />
                  <span>{trends?.reduce((sum: number, day: any) => sum + (day.count || 0), 0) || 0}</span>
                  <span className="text-blue-100 text-sm">total</span>
                </div>
              </div>
            </div>
          </div>

          {/* Chart container with enhanced styling */}
          <div className="h-96 bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-100 shadow-inner">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={trends}
                margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
              >
                <defs>
                  {/* Multi-color gradient for area fill */}
                  <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#1e73be" stopOpacity={0.9}/>
                    <stop offset="50%" stopColor="#3c95b7" stopOpacity={0.6}/>
                    <stop offset="100%" stopColor="#ea7500" stopOpacity={0.2}/>
                  </linearGradient>
                  {/* Gradient for stroke */}
                  <linearGradient id="strokeGradient" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#1e73be"/>
                    <stop offset="50%" stopColor="#3c95b7"/>
                    <stop offset="100%" stopColor="#ea7500"/>
                  </linearGradient>
                  {/* Glow effect */}
                  <filter id="glow">
                    <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                    <feMerge>
                      <feMergeNode in="coloredBlur"/>
                      <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                  </filter>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#e5e7eb"
                  strokeOpacity={0.5}
                  vertical={false}
                />
                <XAxis
                  dataKey="date"
                  tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { weekday: 'short' })}
                  stroke="#6b7280"
                  style={{ fontSize: '13px', fontWeight: 700 }}
                  tickLine={false}
                  axisLine={{ stroke: '#d1d5db', strokeWidth: 2 }}
                  dy={10}
                />
                <YAxis
                  stroke="#6b7280"
                  style={{ fontSize: '13px', fontWeight: 700 }}
                  tickLine={false}
                  axisLine={{ stroke: '#d1d5db', strokeWidth: 2 }}
                  dx={-10}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'rgba(255, 255, 255, 0.98)',
                    border: 'none',
                    borderRadius: '16px',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                    fontWeight: 700,
                    padding: '16px',
                  }}
                  labelStyle={{
                    color: '#1e73be',
                    fontWeight: 700,
                    fontSize: '14px',
                    marginBottom: '8px',
                  }}
                  itemStyle={{
                    color: '#ea7500',
                    fontWeight: 700,
                    fontSize: '16px',
                  }}
                  labelFormatter={(value) => new Date(value).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                  formatter={(value: any) => [`${value} questions`, 'Generated']}
                  cursor={{
                    stroke: '#1e73be',
                    strokeWidth: 2,
                    strokeDasharray: '5 5',
                    fill: 'rgba(30, 115, 190, 0.05)'
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="count"
                  stroke="url(#strokeGradient)"
                  strokeWidth={4}
                  fill="url(#colorCount)"
                  animationDuration={2000}
                  animationEasing="ease-out"
                  dot={{
                    fill: '#ea7500',
                    stroke: '#fff',
                    strokeWidth: 3,
                    r: 6,
                  }}
                  activeDot={{
                    fill: '#1e73be',
                    stroke: '#fff',
                    strokeWidth: 4,
                    r: 8,
                    filter: 'url(#glow)',
                  }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Stats row below chart */}
          <div className="grid grid-cols-4 gap-4 mt-6">
            <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border border-blue-200 text-center hover:shadow-md transition-all">
              <p className="text-xs font-bold text-blue-700 mb-1">Highest Day</p>
              <p className="text-2xl font-bold text-blue-600">
                {trends && trends.length > 0 ? Math.max(...trends.map((d: any) => d.count || 0)) : 0}
              </p>
            </div>
            <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-xl border border-green-200 text-center hover:shadow-md transition-all">
              <p className="text-xs font-bold text-green-700 mb-1">Average</p>
              <p className="text-2xl font-bold text-green-600">
                {trends && trends.length > 0
                  ? Math.round(trends.reduce((sum: number, d: any) => sum + (d.count || 0), 0) / trends.length)
                  : 0}
              </p>
            </div>
            <div className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl border border-purple-200 text-center hover:shadow-md transition-all">
              <p className="text-xs font-bold text-purple-700 mb-1">This Week</p>
              <p className="text-2xl font-bold text-purple-600">
                {trends?.reduce((sum: number, day: any) => sum + (day.count || 0), 0) || 0}
              </p>
            </div>
            <div className="p-4 bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl border border-orange-200 text-center hover:shadow-md transition-all">
              <p className="text-xs font-bold text-orange-700 mb-1">Growth</p>
              <p className="text-2xl font-bold text-orange-600 flex items-center justify-center gap-1">
                <TrendingUp className="w-5 h-5" />
                {trends && trends.length > 1
                  ? Math.round(((trends[trends.length - 1]?.count || 0) / Math.max(trends[0]?.count || 1, 1)) * 100)
                  : 0}%
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Questions */}
      <div className="rounded-2xl border-2 border-gray-100 p-6 bg-gradient-to-br from-white to-blue-50/30 shadow-lg">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Eye className="w-6 h-6 text-blue-600" />
            Recent Questions
          </h2>
          <Link
            to="/questions"
            className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-bold rounded-full hover:bg-blue-200 transition-colors"
          >
            View All
          </Link>
        </div>
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {questionsData && questionsData.length > 0 ? (
            questionsData.slice(0, 5).map((question: any, index: number) => (
              <div
                key={question._id || index}
                className="p-4 bg-white rounded-xl border border-gray-100 hover:border-blue-200 hover:shadow-md transition-all cursor-pointer group"
              >
                <div className="flex items-start justify-between mb-2">
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-bold rounded">
                    {question.question_type || 'Multiple Choice'}
                  </span>
                  <span className="text-xs text-gray-500">
                    {question.created_at ? new Date(question.created_at).toLocaleDateString() : 'Today'}
                  </span>
                </div>
                <p className="text-sm font-medium text-gray-900 line-clamp-2 group-hover:text-blue-600 transition-colors">
                  {question.question_text || question.question || 'Sample question text'}
                </p>
                {question.exam_name && (
                  <p className="text-xs text-gray-500 mt-2">
                    Exam: {question.exam_name}
                  </p>
                )}
              </div>
            ))
          ) : (
            <div className="text-center py-12">
              <Eye className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No questions generated yet</p>
              <Link
                to="/generator-center"
                className="inline-block mt-3 px-4 py-2 bg-gradient-to-r from-[#1e73be] to-[#ea7500] text-white text-sm font-semibold rounded-lg hover:shadow-lg transition-all"
              >
                Generate Questions
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Generation Stats & Top Exams */}
      <div className="grid grid-cols-1 gap-8">
        {/* AI Performance - Hidden */}
        {/* <div className="rounded-2xl border-2 border-gray-100 p-6 bg-gradient-to-br from-white to-green-50/30 shadow-lg">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-green-600" />
              AI Performance
            </h2>
          </div>
          <div className="space-y-4">
            <div className="text-center p-6 bg-white rounded-xl border border-gray-100">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-green-400 to-green-600 rounded-full mb-4">
                <Award className="w-10 h-10 text-white" />
              </div>
              <p className="text-5xl font-bold text-green-600 mb-2">
                {questionsData && questionsData.length > 0
                  ? Math.round((questionsData.filter((q: any) => q.status === 'approved').length / questionsData.length) * 100)
                  : 95}%
              </p>
              <p className="text-sm text-gray-600">Success Rate</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="p-4 bg-blue-50 rounded-xl border border-blue-200 text-center">
                <p className="text-xs font-semibold text-blue-700 mb-1">Avg. Time</p>
                <p className="text-xl font-bold text-blue-600">2.3s</p>
              </div>
              <div className="p-4 bg-purple-50 rounded-xl border border-purple-200 text-center">
                <p className="text-xs font-semibold text-purple-700 mb-1">Quality Score</p>
                <p className="text-xl font-bold text-purple-600">9.2/10</p>
              </div>
            </div>
          </div>
        </div> */}

        {/* Top Exams */}
        <div className="rounded-2xl border-2 border-gray-100 p-6 bg-gradient-to-br from-white to-purple-50/30 shadow-lg">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Award className="w-6 h-6 text-purple-600" />
              Top Performing Exams
            </h2>
            <Link
              to="/exams"
              className="px-3 py-1 bg-purple-100 text-purple-700 text-xs font-bold rounded-full hover:bg-purple-200 transition-colors"
            >
              View All
            </Link>
          </div>
          <div className="space-y-3">
            {examsData && examsData.length > 0 ? (
              examsData.slice(0, 4).map((exam: any, index: number) => {
                const examQuestionCount = questionsData?.filter((q: any) => q.exam_id === exam._id || q.exam_name === exam.name).length || 0;
                return (
                  <div
                    key={exam._id || index}
                    className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-100 hover:border-purple-200 hover:shadow-md transition-all group"
                  >
                    <div className="flex items-center gap-4 flex-1">
                      <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-purple-100 to-purple-200 rounded-lg font-bold text-purple-600 group-hover:scale-110 transition-transform">
                        #{index + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 group-hover:text-purple-600 transition-colors truncate">
                          {exam.name || exam.exam_name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {exam.course_name || exam.course || 'General'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <p className="text-2xl font-bold text-purple-600">{examQuestionCount}</p>
                        <p className="text-xs text-gray-500">Questions</p>
                      </div>
                      <div className="px-3 py-1 bg-purple-100 text-purple-700 text-xs font-bold rounded-full">
                        Active
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-12">
                <Award className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No exams created yet</p>
                <Link
                  to="/exams/create"
                  className="inline-block mt-3 px-4 py-2 bg-gradient-to-r from-[#1e73be] to-[#ea7500] text-white text-sm font-semibold rounded-lg hover:shadow-lg transition-all"
                >
                  Create Exam
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Storage Usage & System Info */}
      <div className="grid grid-cols-1 gap-8">
        {/* Storage Usage - Hidden */}
        {/* <div className="rounded-2xl border-2 border-gray-100 p-6 bg-gradient-to-br from-white to-indigo-50/30 shadow-lg">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <HardDrive className="w-6 h-6 text-indigo-600" />
              Storage Usage
            </h2>
          </div>
          <div className="space-y-4">
            <div className="p-6 bg-white rounded-xl border border-gray-100 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-indigo-100 to-indigo-200 rounded-full mb-4">
                <HardDrive className="w-8 h-8 text-indigo-600" />
              </div>
              <p className="text-4xl font-bold text-indigo-600 mb-1">
                {materialsData ? (materialsData.length * 2.5).toFixed(1) : '0.0'} MB
              </p>
              <p className="text-sm text-gray-600 mb-4">Total Documents Storage</p>
              <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden mb-2">
                <div
                  className="h-3 bg-gradient-to-r from-indigo-400 to-indigo-600 rounded-full transition-all duration-1000"
                  style={{ width: `${Math.min((materialsData?.length || 0) / 100 * 100, 100)}%` }}
                />
              </div>
              <p className="text-xs text-gray-500">
                {materialsData?.length || 0} of 1000 documents
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="w-4 h-4 text-blue-600" />
                  <p className="text-xs font-semibold text-blue-700">PDFs</p>
                </div>
                <p className="text-2xl font-bold text-blue-600">
                  {materialsData?.filter((m: any) => m.file_type === 'pdf' || m.filename?.endsWith('.pdf')).length || materialsData?.length || 0}
                </p>
              </div>
              <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                <div className="flex items-center gap-2 mb-2">
                  <Database className="w-4 h-4 text-green-600" />
                  <p className="text-xs font-semibold text-green-700">Processed</p>
                </div>
                <p className="text-2xl font-bold text-green-600">
                  {materialsData?.filter((m: any) => m.status === 'processed' || m.processing_status === 'completed').length || materialsData?.length || 0}
                </p>
              </div>
            </div>
          </div>
        </div> */}

        {/* System Activity Summary */}
        <div className="rounded-2xl border-2 border-gray-100 p-6 bg-gradient-to-br from-white to-pink-50/30 shadow-lg">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Users className="w-6 h-6 text-pink-600" />
              Activity Summary
            </h2>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-5 bg-white rounded-xl border border-gray-100 hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Calendar className="w-5 h-5 text-blue-600" />
                </div>
                <p className="text-xs font-semibold text-gray-700">Today</p>
              </div>
              <p className="text-3xl font-bold text-blue-600 mb-1">
                {trends && trends.length > 0 ? trends[trends.length - 1]?.count || 0 : 0}
              </p>
              <p className="text-xs text-gray-500">Questions Generated</p>
            </div>

            <div className="p-5 bg-white rounded-xl border border-gray-100 hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                </div>
                <p className="text-xs font-semibold text-gray-700">This Week</p>
              </div>
              <p className="text-3xl font-bold text-green-600 mb-1">
                {trends?.reduce((sum: number, day: any) => sum + (day.count || 0), 0) || 0}
              </p>
              <p className="text-xs text-gray-500">Total Generated</p>
            </div>

            <div className="p-5 bg-white rounded-xl border border-gray-100 hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Users className="w-5 h-5 text-purple-600" />
                </div>
                <p className="text-xs font-semibold text-gray-700">Active Users</p>
              </div>
              <p className="text-3xl font-bold text-purple-600 mb-1">1</p>
              <p className="text-xs text-gray-500">Currently Online</p>
            </div>

            <div className="p-5 bg-white rounded-xl border border-gray-100 hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Sparkles className="w-5 h-5 text-orange-600" />
                </div>
                <p className="text-xs font-semibold text-gray-700">Avg Quality</p>
              </div>
              <p className="text-3xl font-bold text-orange-600 mb-1">9.2</p>
              <p className="text-xs text-gray-500">Out of 10</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
